import type { ValuePropCard, ValuePropsContent } from '@/content/home';
import { Eye, Link2, ShieldCheck, Sparkles } from '@/components/ui/Icons';

const ICON_MAP = {
  'shield-check': ShieldCheck,
  'link-2': Link2,
  eye: Eye,
  sparkles: Sparkles,
} as const;

export type ValuePropGridProps = {
  content: ValuePropsContent;
};

export function ValuePropGrid({ content }: ValuePropGridProps) {
  return (
    <section
      aria-labelledby="why-heading"
      className="border-y border-rule bg-page px-6 py-[120px] md:px-[72px]"
    >
      <div className="mx-auto flex max-w-[1296px] flex-col items-center gap-16">
        <div className="flex w-full max-w-[760px] flex-col items-center gap-5 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-pencil">
            {content.eyebrow}
          </p>
          <h2
            id="why-heading"
            className="text-[34px] font-bold leading-[1.15] tracking-[-1px] text-ink md:text-[42px]"
          >
            {content.heading}
          </h2>
          <p className="text-[16px] leading-[1.55] text-graphite md:text-[17px]">
            {content.subhead}
          </p>
        </div>

        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
          {content.cards.map((card, idx) => (
            <ValueCard key={idx} card={card} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ValueCard({ card }: { card: ValuePropCard }) {
  const Icon = ICON_MAP[card.icon];
  return (
    <article className="flex flex-col gap-5 rounded-[14px] border border-rule bg-canvas p-9">
      <span
        aria-hidden="true"
        className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-cloak-tint text-cloak-accent"
      >
        <Icon size={22} />
      </span>
      <h3 className="text-[20px] font-bold leading-[1.25] tracking-[-0.3px] text-ink md:text-[21px]">
        {card.title}
      </h3>
      <p className="text-[15px] leading-[1.6] text-graphite">{card.body}</p>
      {card.visual === 'identity-map' && <IdentityMapVisual />}
      {card.visual === 'screenshot-008' && (
        <img
          src="/product-screenshots/008-cloakling.png"
          alt=""
          aria-hidden="true"
          className="mt-2 h-[154px] w-full rounded-[10px] border border-rule object-cover"
        />
      )}
    </article>
  );
}

function IdentityMapVisual() {
  return (
    <div
      aria-hidden="true"
      className="mt-2 flex flex-col gap-2 rounded-[10px] border border-rule bg-page p-3"
    >
      <div className="flex items-center justify-between rounded-md border border-rule bg-canvas px-3 py-2">
        <span className="text-[13px] font-medium text-ink">Jane Doe → Adam Flores</span>
        <span className="text-[10px] font-semibold text-pencil">1×</span>
      </div>
      <div className="flex items-center justify-between rounded-md border border-cloak-accent/30 bg-cloak-tint px-3 py-2">
        <span className="text-[13px] font-medium text-ink">John Smith → Michael Holloway</span>
        <span className="text-[10px] font-semibold text-cloak-accent">10×</span>
      </div>
      <p className="px-1 text-[10px] text-pencil">Realistic Substitute</p>
    </div>
  );
}
